import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Target, Heart, Users, Award, TrendingUp, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const AboutPage = () => {
  const { formatPrice } = useCurrency();
  const values = [
    {
      icon: Heart,
      title: "Customer Success First",
      description: "Your success is our success. Every feature we build is designed to help local businesses thrive, not just survive."
    },
    {
      icon: Target,
      title: "Simplicity in Complexity",
      description: "We take enterprise-level features and make them simple enough for any business owner to use without a manual."
    },
    {
      icon: Users,
      title: "Local Business Focus",
      description: "We're not trying to be everything to everyone. We're laser-focused on helping local service businesses grow."
    },
    {
      icon: Award,
      title: "Proven Results",
      description: "Every feature is tested and proven to drive real business growth, not just vanity metrics."
    }
  ];

  const stats = [
    { number: "5,000+", label: "Businesses Served", icon: Users },
    { number: "40%", label: "Average Lead Increase", icon: TrendingUp },
    { number: "60%", label: "Time Saved on Admin", icon: Clock },
    { number: "98%", label: "Customer Satisfaction", icon: Award }
  ];

  const timeline = [
    {
      year: "2022",
      title: "The Problem We Saw",
      description: "Local businesses were drowning in multiple tools, losing leads, and working 80-hour weeks just to stay afloat."
    },
    {
      year: "2023",
      title: "Building the Solution",
      description: "We spent a year talking to hundreds of business owners, understanding their pain points, and building our first version."
    },
    {
      year: "2024",
      title: "Proven Success",
      description: "Today, we help over 5,000 local businesses capture more leads, automate their follow-up, and reclaim their time."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Helping Local Businesses
                <span className="text-[#FF5A3D]"> Become Local Legends</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                We believe every local business owner deserves the tools to compete with the big players, 
                without the complexity or cost.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  asChild
                  className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Link to="/pricing" className="flex items-center space-x-2">
                    <span>Join the Movement</span>
                    <ArrowRight size={20} />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#0E163B] px-8 py-4 rounded-xl">
                  <a href="/contact">Get in Touch</a>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 className="text-xl font-semibold mb-6 text-center">Our Mission</h3>
                <div className="space-y-4 text-center">
                  <div className="p-4 bg-white/10 rounded-lg">
                    <h4 className="font-semibold mb-2">Simplify Growth</h4>
                    <p className="text-sm text-gray-300">One system, unlimited possibilities</p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-lg">
                    <h4 className="font-semibold mb-2">Automate Success</h4>
                    <p className="text-sm text-gray-300">Work smarter, not harder</p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-lg">
                    <h4 className="font-semibold mb-2">Empower Owners</h4>
                    <p className="text-sm text-gray-300">Get your time and life back</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#FF5A3D]/10 text-[#FF5A3D] px-4 py-2 rounded-full mb-6">
              Our Impact
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Real Numbers, Real Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These aren't just metrics — they represent thousands of business owners who've transformed their growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center p-8 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-[#FF5A3D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <stat.icon size={32} className="text-[#FF5A3D]" />
                  </div>
                  <div className="text-4xl font-bold text-[#0E163B] mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Story Timeline */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full mb-6">
              Our Story
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              From Problem to Solution
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every great solution starts with understanding a real problem.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-1 bg-[#FF5A3D]/20"></div>
            
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Timeline Dot */}
                  <div className="absolute left-6 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 bg-[#FF5A3D] rounded-full border-4 border-white shadow-lg z-10"></div>
                  
                  {/* Content */}
                  <div className={`w-full md:w-5/12 ml-16 md:ml-0 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <CardContent className="pt-6">
                        <Badge className="bg-[#FF5A3D] text-white mb-4">{item.year}</Badge>
                        <h3 className="text-xl font-bold text-[#0E163B] mb-3">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-green-100 text-green-800 px-4 py-2 rounded-full mb-6">
              Our Values
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              What Drives Us Every Day
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These aren't just words on a wall — they guide every decision we make and every feature we build.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-[#FF5A3D]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#FF5A3D] group-hover:text-white transition-colors">
                    <value.icon size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#0E163B] mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full mb-6">
                Meet the Team
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Built by Business Owners, for Business Owners
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our team isn't just developers and marketers — we're business owners ourselves. 
                We've felt the pain of juggling multiple tools, losing leads, and working endless hours.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "Former local business owners who understand your challenges",
                  "Customer success experts dedicated to your growth",
                  "Technical team focused on simplicity and reliability",
                  "Support staff available when you need help most"
                ].map((point, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle size={20} className="text-[#FF5A3D] flex-shrink-0" />
                    <span className="text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                asChild
                className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-semibold px-8 py-3 rounded-lg"
              >
                <a href="/contact">Get to Know Us Better</a>
              </Button>
            </div>
            
            <div className="relative">
              {/* Team Photo */}
              <div className="mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1425421669292-0c3da3b8f529?w=600&h=400&fit=crop&crop=center" 
                  alt="Local Business Heroes Team" 
                  className="rounded-2xl shadow-xl w-full h-64 object-cover"
                />
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">Our Commitment to You</h3>
                <div className="space-y-6">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <h4 className="font-semibold text-[#0E163B] mb-2">Always Improving</h4>
                    <p className="text-sm text-gray-600">We release new features and improvements every month based on your feedback.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <h4 className="font-semibold text-[#0E163B] mb-2">Real Support</h4>
                    <p className="text-sm text-gray-600">When you need help, you'll talk to real people who understand your business.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <h4 className="font-semibold text-[#0E163B] mb-2">Your Success</h4>
                    <p className="text-sm text-gray-600">We measure our success by your success. If you're not growing, we're not doing our job.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Become a Local Legend?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of local business owners who've already transformed their growth with Local Business Heroes.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              asChild
              className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              <a href="/pricing">
                Start Your 14-Day Trial for {formatPrice(5)}
              </a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF5A3D] px-8 py-4 rounded-xl">
              <a href="/features">See How It Works</a>
            </Button>
          </div>
          
          <p className="text-sm opacity-75">
            Just {formatPrice(5)} to start · Cancel anytime · Full support included
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;